/**
 * Outreach agent step graph.
 *
 * Mirrors the shape of src/lib/report-generation/step-graph.ts: typed step keys
 * plus an explicit dependency map. The graph being declarative is what lets the
 * UI render the whole plan greyed-out before the run starts and light rows up
 * as they finish — a checklist rather than an append-only log.
 *
 * Unlike report generation this graph is strictly linear, so it needs no
 * parallel scheduler. The dependency map is kept anyway because it documents
 * the data flow and lets the UI order rows without hardcoding a sequence.
 */

import type { OutreachGenerationStep, OutreachStepKey, OutreachStepKeyV2, OutreachStepKeyV3 } from './types';

export const outreachStepOrder: OutreachStepKeyV2[] = [
  'CLASSIFY_RECIPIENT',
  'CHOOSE_STRATEGY',
  'DRAFT',
  'CRITIQUE',
  'REVISE',
];

export const outreachStepDependencies: Record<OutreachStepKeyV2, OutreachStepKeyV2[]> = {
  CLASSIFY_RECIPIENT: [],
  CHOOSE_STRATEGY: ['CLASSIFY_RECIPIENT'],
  DRAFT: ['CHOOSE_STRATEGY'],
  CRITIQUE: ['DRAFT'],
  REVISE: ['CRITIQUE'],
};

export const outreachV3StepOrder: OutreachStepKeyV3[] = [
  'PREPARE',
  'COMPOSE',
  'EVALUATE',
];

/**
 * User-facing labels. Written as present-progressive status lines because they
 * are read while the step is running, not afterwards.
 */
export const outreachStepLabels: Record<OutreachStepKey, string> = {
  CLASSIFY_RECIPIENT: 'Working out who this is',
  CHOOSE_STRATEGY: 'Choosing the angle and the ask',
  DRAFT: 'Writing the message',
  CRITIQUE: 'Checking it against the brief',
  REVISE: 'Fixing what the check found',
  PREPARE: 'Understanding the recipient',
  COMPOSE: 'Thinking and writing',
  EVALUATE: 'Checking quality',
};

/**
 * Longer explanations for the expanded progress view, so a user can understand
 * what the agent decided rather than just that it decided something.
 */
export const outreachStepDescriptions: Record<OutreachStepKey, string> = {
  CLASSIFY_RECIPIENT:
    'Reads the title and context to work out whether this is a recruiter, a hiring manager, a peer, or someone whose role we cannot tell.',
  CHOOSE_STRATEGY:
    'Decides tone, length, the angle, and what to ask for — then names what we do not know so the message does not invent it.',
  DRAFT: 'Writes to that brief.',
  CRITIQUE:
    'A different model checks the draft for invented facts, filler, and whether the ask fits the recipient.',
  REVISE: 'Applies the fixes. Skipped when the draft passes.',
  PREPARE:
    'Reads the recipient\'s title and context to understand who they are, what role they play, and how warm the relationship is.',
  COMPOSE:
    'A reasoning model thinks through the full outreach playbook — which category fits, what strategy to use, what material is available — and writes the message in one pass.',
  EVALUATE:
    'A separate model checks the message for fabrication, filler, ask-fit, and tone. Runs after the message is shown so you see it immediately.',
};

/**
 * All steps PENDING — the plan, published before any work begins so the UI can
 * render the whole checklist up front.
 *
 * Lives here rather than in run.ts on purpose: client components need it, and
 * run.ts transitively imports the model steps (and therefore @ai-sdk/openai and
 * the API key). Importing it from run.ts would drag the entire server-side agent
 * into the browser bundle.
 */
export function initialOutreachSteps(): OutreachGenerationStep[] {
  return outreachV3StepOrder.map((step_key) => ({ step_key, status: 'PENDING' }));
}

export function initialOutreachV2Steps(): OutreachGenerationStep[] {
  return outreachStepOrder.map((step_key) => ({ step_key, status: 'PENDING' }));
}

export function initialOutreachV3Steps(): OutreachGenerationStep[] {
  return outreachV3StepOrder.map((step_key) => ({ step_key, status: 'PENDING' }));
}

/** Bump alongside outreachDraftCache PROMPT_VERSION when prompts or the graph change. */
export const OUTREACH_AGENT_VERSION = 'v3';
